import { useState } from 'react';
import {
  useNpsNetworkSummaryQuery,
  npsColor,
  type NpsGroupAgg,
} from '../api/nps';
import NpsScoreTile from '../components/nps/NpsScoreTile';
import NpsMap from '../components/nps/NpsMap';

type Grouping = 'territory' | 'region' | 'state';
const GROUP_LABEL: Record<Grouping, string> = {
  territory: 'Territory',
  region: 'Region (FBC)',
  state: 'State',
};

/**
 * The franchisor ("our side") NPS view: network-wide scores by audience, a US
 * map of NPS by location, and rollup tables by territory, region, and state.
 * Clicking a location drills into that location's detail.
 */
export default function NpsAdmin({ onDrillIn }: { onDrillIn: (af: string) => void }) {
  const { data, isLoading, error } = useNpsNetworkSummaryQuery(true);
  const [group, setGroup] = useState<Grouping>('territory');

  if (isLoading) return <div className="nps-respond-muted">Loading network NPS…</div>;
  if (error) return <div className="nps-respond-error">Couldn’t load: {(error as Error).message}</div>;
  if (!data) return null;

  const minN = data.minResponsesToShow;
  const rows: NpsGroupAgg[] =
    group === 'territory' ? data.byTerritory : group === 'region' ? data.byRegion : data.byState;

  return (
    <div className="nps-page">
      <div className="ds-section-header">
        <h2>Net Promoter Score — Network</h2>
        <span className="meta">
          {data.locationsWithResponses} locations with responses · scores shown at {minN}+ responses
        </span>
      </div>

      {/* Network-wide by audience */}
      <div className="nps-tile-grid">
        {data.byAudience.map((a) => (
          <NpsScoreTile key={a.audience} title={a.label} summary={a} minResponses={minN} />
        ))}
      </div>

      {/* Map */}
      <div className="ds-section-header" style={{ marginTop: 24 }}>
        <h2>NPS by location</h2>
        <span className="meta">Color = NPS band · click a location to drill in</span>
      </div>
      <NpsMap points={data.mapPoints} onDrillIn={onDrillIn} />

      {/* Rollups */}
      <div className="ds-section-header" style={{ marginTop: 24 }}>
        <h2>NPS rankings</h2>
        <div className="nps-seg">
          {(Object.keys(GROUP_LABEL) as Grouping[]).map((g) => (
            <button
              key={g}
              type="button"
              className={`nps-seg-btn${group === g ? ' active' : ''}`}
              onClick={() => setGroup(g)}
            >
              {GROUP_LABEL[g]}
            </button>
          ))}
        </div>
      </div>
      <div className="ds-panel nps-rollup">
        <table>
          <thead>
            <tr>
              <th>{GROUP_LABEL[group]}</th>
              <th className="num">NPS</th>
              <th className="num">Responses</th>
              <th className="num">Promoters</th>
              <th className="num">Passives</th>
              <th className="num">Detractors</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td className="num">
                  {r.nps == null ? (
                    <span className="nps-muted">{r.belowThreshold ? `< ${minN}` : '—'}</span>
                  ) : (
                    <strong style={{ color: npsColor(r.nps) }}>{r.nps > 0 ? `+${r.nps}` : r.nps}</strong>
                  )}
                </td>
                <td className="num">{r.responses}</td>
                <td className="num">{r.promoters}</td>
                <td className="num">{r.passives}</td>
                <td className="num">{r.detractors}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="nps-muted">No responses yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
